import { useState } from 'react';
import {
  List,
  Box,
  Typography,
  Select,
  MenuItem as MuiMenuItem,
  Grid,
} from '@mui/material';
import {
  AddIcon,
  PersonFilledIcon,
  SettingsIcon,
} from 'components/common/Icon/CommonIcon';
import { useFlags } from 'hooks/useFeatureFlag';
import { FormationsGhostButton } from 'components/common/Buttons';
import { Link } from 'components/Link';
import { Routes } from 'fnRoutes';
import { MAIN_COLOR } from 'theme/constant';
import { Business, BusinessPlan } from 'models/business';
import { CONSTANTS, ENTITY_MAPPING } from 'constants/common';
import { queryClient } from 'states/reactQueryClient';
import { showErrorToast } from 'components/toast/showToast';
import logoFull from 'icons/formationsLogo/formationslogo-full-black-large.png';
import smallLogo from 'components/common/Icon/assets/formationsLogo.png';
import { IAccount } from 'models/account';
import {
  useCurrentUser,
  useIndustryOptions,
  useNavigate,
  useSpoof,
} from 'hooks/api';
import { FormationsFormDialog } from 'components/common/FormationsFormDialog';
import { useCompanyByUserId, useCreateCompany } from 'hooks/api/useCompanies';
import * as yup from 'yup';
import { useFormationsAuth } from 'auth/FormationsAuthProvider';
import { useCurrentUserFeatureList } from 'hooks/api/useFeatures';
import {
  IFormField,
  FormationsFormFields,
} from 'components/common/FormationsForm2';
import { IAction } from '../constant';
import { filterVisibleActions } from '../helpers';
import { MenuItem } from './MenuItem';
import { AccountMenuItem } from './AccountMenuItem';
import { useSidebarOpen } from './sidebarContext';
import { SetupProgress } from './SetupProgress';

export const createBusinessSchema = yup.object().shape({
  industryCode: yup.string().required('Please select an industry.'),
  entityType: yup.string().required('Please select an entity type.'),
  formationsPlan: yup.string().required('Please select a plan.'),
});

interface Props {
  actions: IAction[];
}

export const isOwner = (
  business: Business,
  accounts: IAccount[],
  userId?: string,
) => {
  if (!userId) {
    return false;
  }
  const account = accounts.find((a) => business.accountId === a.id);
  return account?.ownerUserId === userId;
};

export const AdminTopHeader = ({ actions }: Props) => {
  const { currentUser } = useCurrentUser();
  const { isAdminSpoof } = useSpoof();
  const home = isAdminSpoof ? Routes.CUSTOMER_HOME : Routes.HOME();

  const { company } = useCompanyByUserId(
    currentUser?.role === CONSTANTS.USER_ROLES.CUSTOMER
      ? currentUser?.id
      : undefined,
  );
  // feature flags
  const flags = useFlags();
  const featureList = useCurrentUserFeatureList();
  const enabledActions = filterVisibleActions(actions, flags, featureList);
  const open = useSidebarOpen();

  return (
    <Box>
      <Box p={4} mb={4} sx={{ opacity: open ? 1 : 0 }}>
        <Box data-testid="logo">
          <Link to={home}>
            <img
              src={logoFull}
              alt="formations"
              width="168px"
              color={MAIN_COLOR}
            />
          </Link>
        </Box>
        <Box mt={1}>
          <Typography
            variant="body1S"
            component="p"
            sx={{
              whiteSpace: 'break-spaces',
              wordWrap: 'break-word',
              fontSize: '14px',
            }}
          >
            {company?.name}
          </Typography>
        </Box>
      </Box>
      <List>
        {enabledActions.map((action) => (
          <MenuItem
            key={action.id}
            {...action}
            active={action.id === 'welcome'}
          />
        ))}
      </List>
    </Box>
  );
};
